import React from 'react';
import Dropdowns from '../../components/Dropdowns';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import { useCustomerDataFilters } from 'contexts/CustomerDataFiltersContext';
import { fetchKeyAccountsData } from './api/index';
import CustomerCard from '../customer-data/components/CustomerCard';
import { useSmoothLoading } from 'lib/use-smooth-loading';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { Search } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CustomerData } from '../customer-data/api';

const KeyAccountsPage: React.FC = () => {
    const lastQueryRef = React.useRef<string | null>(null);
    const navigate = useNavigate();
    const { setBreadcrumbs } = useBreadcrumbs();
    const {
        years,
        year,
        levels,
        loading,
        error,
        handleSelectChange,
        handleBatchSelect,
        handleYearChange,
        getCurrentFilters,
    } = useCustomerDataFilters();

    // Data state
    const [data, setData] = React.useState<CustomerData[]>([]);
    const [total, setTotal] = React.useState<number>(0);
    const [dataLoading, setDataLoading] = React.useState<boolean>(false);
    const [dataError, setDataError] = React.useState<string>('');
    const [hasFetchedOnce, setHasFetchedOnce] = React.useState<boolean>(false);

    // UI state
    const [searchParams, setSearchParams] = useSearchParams();
    const initialSearch = searchParams.get('search') || '';
    const [searchTerm, setSearchTerm] = React.useState<string>(initialSearch);
    const [conversionType, setConversionType] = React.useState<'INR' | 'Lakhs' | 'Crores'>('Lakhs');
    const [sortBy] = React.useState<string>('total_income');
    const [sortDir] = React.useState<'asc' | 'desc'>('desc');
    const [page, setPage] = React.useState<number>(1);
    const [pageSize] = React.useState<number>(20);

    const { isLoading: smoothLoading, showContent } =
        useSmoothLoading(dataLoading, {
            minLoadingTime: 200,
            debounceTime: 200,
        });

    const fetchData = React.useCallback(
        async (search?: string, currentPage = 1) => {
            if (!year) return;

            const filters = getCurrentFilters();
            const queryKey = JSON.stringify({
                year,
                filters,
                search: search?.trim() || '',
                page: currentPage,
                page_size: pageSize,
                sortBy,
                sortDir,
            });

            if (lastQueryRef.current === queryKey) return;
            lastQueryRef.current = queryKey;

            setDataLoading(true);
            setDataError('');

            try {
                const res = await fetchKeyAccountsData({
                    year,
                    filters,
                    ...(search && search.trim() && { search: search.trim() }),
                    sort: { by: sortBy, dir: sortDir },
                    page: currentPage,
                    page_size: pageSize,
                });

                setData(res.customers);
                setTotal(res.total);
            } catch (err: unknown) {
                const message =
                    err instanceof Error
                        ? err.message
                        : 'Failed to fetch key accounts';
                setDataError(message);
                setData([]);
                setTotal(0);
            } finally {
                setDataLoading(false);
                setHasFetchedOnce(true);
            }
        },
        [getCurrentFilters, year, pageSize, sortBy, sortDir]
    );

    React.useEffect(() => {
        setBreadcrumbs([
            { label: 'Reports', href: '/reports/pnl' },
            { label: 'Key Accounts' },
        ]);
    }, [setBreadcrumbs]);

    React.useEffect(() => {
        const urlSearch = searchParams.get('search') || '';
        setSearchTerm(urlSearch);
    }, [searchParams]);

    React.useEffect(() => {
        if (!year) return;
        setData([]);
        setTotal(0);
        setPage(1);
        // setSearchTerm('');
    }, [year]);

    React.useEffect(() => {
        if (!year || levels.length === 0) return;
        setPage(1);
        fetchData(searchTerm || undefined, 1);
    }, [year, levels, fetchData]);

    const handleSearch = React.useCallback(
        (search: string) => {
            setPage(1);
            fetchData(search, 1);
        },
        [fetchData]
    );

    const debouncedSearchRef =
        React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const debouncedSearch = React.useCallback(
        (search: string) => {
            if (debouncedSearchRef.current) {
                clearTimeout(debouncedSearchRef.current);
            }
            debouncedSearchRef.current = setTimeout(() => {
                handleSearch(search);
            }, 300);
        },
        [handleSearch]
    );

    React.useEffect(() => {
        return () => {
            if (debouncedSearchRef.current) {
                clearTimeout(debouncedSearchRef.current);
            }
        };
    }, []);

    const handlePageChange = React.useCallback(
        (newPage: number) => {
            setPage(newPage);
            fetchData(searchTerm, newPage);
        },
        [fetchData, searchTerm]
    );

    const handleCustomerClick = React.useCallback(
        (customerCode: string, customerName: string) => {
            const params = new URLSearchParams({
                customerCode,
                customerName,
            });
            navigate(`/reports/key-accounts/branches?${params.toString()}`);
        },
        [navigate]
    );

    const totalPages = Math.ceil(total / pageSize);

    return (
        <div className="space-y-6 mx-auto w-full max-w-screen-2xl 2xl:max-w-[2000px] px-4 sm:px-6">
            {/* Filters */}
            <div className="space-y-4">
                <Dropdowns
                    year={year}
                    years={years}
                    levels={levels}
                    loading={loading}
                    onSelectChange={handleSelectChange}
                    onBatchSelect={handleBatchSelect}
                    onYearChange={handleYearChange}
                />

                {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-red-600 text-sm">{error}</p>
                    </div>
                )}
            </div>

            {/* Search + Controls */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                        placeholder="Search key account..."
                        value={searchTerm}
                        onChange={(e) => {
                            const val = e.target.value;
                            setSearchTerm(val);

                            const next = new URLSearchParams(searchParams);
                            if (val.trim()) {
                                next.set('search', val.trim());
                            } else {
                                next.delete('search');
                            }
                            setSearchParams(next);

                            debouncedSearch(val);
                        }}
                        className="pl-10"
                    />
                </div>

                <div className="flex gap-2">
                    {(['INR', 'Lakhs', 'Crores'] as const).map((type) => (
                        <button
                            key={type}
                            className={`px-3 py-1 text-sm rounded-md transition-colors ${conversionType === type
                                    ? 'bg-black text-white'
                                    : 'bg-white border border-black hover:bg-gray-200'
                                }`}
                            onClick={() => setConversionType(type)}
                        >
                            {type}
                        </button>
                    ))}
                </div>
            </div>

            {/* Errors */}
            {dataError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-red-600 text-sm">{dataError}</p>
                </div>
            )}

            {/* Loading */}
            {smoothLoading && (
                <div className="space-y-6">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="bg-white rounded-lg border shadow-sm">
                            <div className="bg-gray-200 animate-pulse h-16 rounded-t-lg" />
                            <div className="p-6 space-y-4">
                                <div className="h-4 bg-gray-200 animate-pulse rounded w-3/4" />
                                <div className="h-4 bg-gray-200 animate-pulse rounded w-1/2" />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Empty */}
            {!smoothLoading && hasFetchedOnce && data.length === 0 && !dataError && (
                <div className="text-center py-12 text-gray-500">
                    No Key Accounts Assigned Yet
                </div>
            )}

            {/* Data */}
            {!smoothLoading && data.length > 0 && showContent && (
                <div className="space-y-6">
                    <div className="text-sm text-gray-600">
                        Showing {data.length} of {total} key accounts
                        {searchTerm && ` for "${searchTerm}"`}
                    </div>

                    <div className="space-y-6">
                        {data.map((customer) => (
                            <CustomerCard
                                key={customer.customer_code}
                                customer={customer}
                                conversionType={conversionType}
                                onClick={handleCustomerClick}
                            />
                        ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex justify-center gap-2 mt-8">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePageChange(page - 1)}
                                disabled={page <= 1}
                            >
                                Previous
                            </Button>

                            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 5) pageNum = i + 1;
                                else if (page <= 3) pageNum = i + 1;
                                else if (page >= totalPages - 2)
                                    pageNum = totalPages - 4 + i;
                                else pageNum = page - 2 + i;

                                return (
                                    <Button
                                        key={pageNum}
                                        variant={page === pageNum ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => handlePageChange(pageNum)}
                                    >
                                        {pageNum}
                                    </Button>
                                );
                            })}

                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePageChange(page + 1)}
                                disabled={page >= totalPages}
                            >
                                Next
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default KeyAccountsPage;
